import React, { useCallback, useReducer } from 'react'
import { useChargingSessions } from '../../charging-sessions/hooks/use-charging-sessions'
import { DashboardSkeleton } from './DashboardSkeleton'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Filter } from '@e3dc-react/shell/Elements/Filter'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { currencyCodeToSymbol, useFetchCountryInfo, useHagerBridgePermissions } from 'src/Libs/e-mobility/shared'
import { BannerKey, BannerLocations, BannerQueue } from 'src/Libs/banners'
import { DashboardChartContainer } from './chart/DashboardChartContainer'
import { StatisticCards } from './statistic-cards/StatisticCards'
import { useAuthenticationFilterOptions } from '../../shared/hooks/filter-options/use-authentication-filter-options'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import { ClearFiltersButton } from '../../shared/components/ClearFiltersButton/ClearFiltersButton'
import { ChartDateRange, DashboardFilterAction, DashboardFilterActionType, DashboardState, MetricType } from '../models/dashboard.model'
import { useWallboxFilterOptions } from '../../shared/hooks/filter-options/use-wallbox-filter-options'
import { WallboxType } from '../../wallboxes/models/wallbox-model'
import { useChargingSessionsStatusFilterOptions } from '../../shared/hooks/filter-options/use-charging-sessios-status-filter-options'
import { ChargingSessionStatusFilter } from '../../shared/components/ChargingSessionStatusFilter'
import { useTypedSelector } from 'src/State/RootReducer'
import { endOfMonth, isAfter, startOfMonth } from '@e3dc-react/shell/Libs/date'
import { ChartDateRangeFilter } from './ChartDateRangeFilter'
import { useDispatch } from 'react-redux'
import { EMobilityActionType } from 'src/State/E-Mobility/EMobilityStateModel'
import { DashboardMetricSelector } from './chart/DashboardMetricSelector'
import moment from 'moment'
import { useShouldShowEMobilityTourBanner } from '../../guided-tour'
import { getIsBannerRemovedByUser } from 'src/Libs/banners/hooks/use-banner'
import { useUserSettings } from 'src/Hooks'

interface DashboardProps {
  installationId: number
}

const defaultCurrencyCode = 'EUR'
const headerHeight = '145px'

/**
 * Ensure that when resetting state, we create a new object with fields being new references. Otherwise,
 * date-fns can subsequently modify the initial values and dashboard would show incorrect date range,
 */
const generateDashboardInitialState: () => DashboardState = () => ({
  metric: MetricType.Energy,
  filters: {
    wallboxId: [],
    ocpiTokenIds: [],
    statuses: [],
    dataRange: {
      startDate: startOfMonth(new Date()).toISOString(),
      endDate: endOfMonth(new Date()).toISOString(),
      view: 'month',
    },
  },
})

function dashboardStateReducer(state: DashboardState, action: DashboardFilterAction): DashboardState {
  switch (action.type) {
    case DashboardFilterActionType.setAuthentication:
      return { ...state, filters: { ...state.filters, ocpiTokenIds: action.value } }
    case DashboardFilterActionType.setStatus:
      return {
        ...state,
        filters: { ...state.filters, statuses: action.value },
      }
    case DashboardFilterActionType.setWallbox:
      return { ...state, filters: { ...state.filters, wallboxId: action.value } }
    case DashboardFilterActionType.dateRange:
      return { ...state, filters: { ...state.filters, dataRange: action.value } }
    case DashboardFilterActionType.clearAll:
      return generateDashboardInitialState()
    default:
      return state
  }
}

/**
 * Entry component for KPI statistics dashboard.
 * @param - installationId as string
 */
export const Dashboard: React.FC<DashboardProps> = ({ installationId }) => {
  const [dashboardState, dispatch] = useReducer(dashboardStateReducer, generateDashboardInitialState())
  const { t } = useCustomTranslation()
  const { permissions } = useHagerBridgePermissions()
  const installation = useTypedSelector(state => state.storage.installation)
  const dashboardMetric = useTypedSelector(state => state.eMobility.dashboard.metric)
  const storeDispatch = useDispatch()
  const shouldShowEMobilityTourBanner = useShouldShowEMobilityTourBanner()
  const { userSettings } = useUserSettings()
  const isTourBannerVisible = shouldShowEMobilityTourBanner && !getIsBannerRemovedByUser(BannerKey.EmobilityTourBanner, userSettings)

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {
    data,
    unfilteredData,
    isLoading: isSessionsLoading,
    isError: isSessionsError,
  } = useChargingSessions({
    installationId,
    ocpiTokenIds: dashboardState.filters.ocpiTokenIds,
    status: dashboardState.filters.statuses,
    wallboxId: dashboardState.filters.wallboxId,
    midMeter: [],
    startDate: dashboardState.filters.dataRange.startDate,
    endDate: dashboardState.filters.dataRange.endDate,
  })

  const { wallboxOptions, isLoadingWallboxes } = useWallboxFilterOptions(unfilteredData, installationId)
  const { authenticationOptions, isChargingCardsLoading } = useAuthenticationFilterOptions(unfilteredData, installationId)
  const { country } = useFetchCountryInfo(installation?.countryCode ?? '', installationId, Boolean(installation?.decommissionedAt))

  const currencyCode = country?.currencyCode || defaultCurrencyCode
  const currencySymbol = currencyCodeToSymbol(currencyCode)

  const hasColumnFilters =
    dashboardState.filters.ocpiTokenIds.length > 0 || dashboardState.filters.statuses.length > 0 || dashboardState.filters.wallboxId.length > 0
  const includesBiDiSessions = Boolean(unfilteredData?.some(session => session.wallbox.type === WallboxType.Bidi))
  const isLoading = isSessionsLoading || isLoadingWallboxes || isChargingCardsLoading

  const statusFilterOptions = useChargingSessionsStatusFilterOptions(unfilteredData)

  const firstSessionCreated =
    unfilteredData && unfilteredData.length > 0
      ? unfilteredData.toSorted((sessionStartA, sessionStartB) =>
          isAfter(new Date(sessionStartA.startDateTime), new Date(sessionStartB.startDateTime)) ? 1 : -1
        )[0]
      : undefined

  const handleMetricChange = useCallback(
    (value: MetricType) => {
      storeDispatch({
        type: EMobilityActionType.SetDashboardMetric,
        payload: value,
      })
    },
    [storeDispatch]
  )

  const handleRangeChange = useCallback(
    (range: ChartDateRange) => {
      dispatch({
        type: DashboardFilterActionType.dateRange,
        value: range,
      })
    },
    [dispatch]
  )

  const dashboardGraphMinHeight = (): number => {
    if (isTourBannerVisible && isMobile) {
      return 300
    }

    if (!isTourBannerVisible && isMobile) {
      return 100
    }

    if (isTourBannerVisible && isTablet) {
      return 150
    }

    return 0
  }

  if (isLoading || !currencyCode) {
    return <DashboardSkeleton />
  }

  if (isSessionsError) {
    return <ErrorMessage title={t('emobility.chargingSession.errorMessage')} />
  }

  return (
    <Stack flexDirection="column" justifyContent="space-between" height={`calc(100vh - ${headerHeight} + ${dashboardGraphMinHeight()}px)`}>
      {permissions.tourGuidePermissions.viewTourGuide && <BannerQueue location={BannerLocations.EmobilityTour} />}

      <ChartDateRangeFilter
        value={dashboardState.filters.dataRange}
        onChange={handleRangeChange}
        minDate={firstSessionCreated ? new Date(firstSessionCreated.startDateTime) : undefined}
        maxDate={moment()}
        leftSlot={<DashboardMetricSelector value={dashboardMetric} currencySymbol={currencySymbol!} onChange={handleMetricChange} />}
      />

      <Stack direction="row" flexWrap={'wrap'} gap={2} id="e-mobility-dashboard-table-filters" mt={2} mb={2}>
        <Filter
          label={t('emobility.chargingSession.wallbox')}
          options={wallboxOptions}
          value={dashboardState.filters.wallboxId}
          onChange={value => {
            dispatch({ type: DashboardFilterActionType.setWallbox, value })
          }}
          disabled={isLoading}
        />
        <Filter
          label={t('emobility.chargingSession.authentication')}
          options={authenticationOptions}
          value={dashboardState.filters.ocpiTokenIds}
          onChange={value => {
            dispatch({ type: DashboardFilterActionType.setAuthentication, value })
          }}
          disabled={isLoading}
        />
        <ChargingSessionStatusFilter
          value={dashboardState.filters.statuses}
          onChange={value => {
            dispatch({ type: DashboardFilterActionType.setStatus, value })
          }}
          disabled={isLoading}
          options={statusFilterOptions}
        />
        {hasColumnFilters ? (
          <ClearFiltersButton
            onClick={() => {
              dispatch({ type: DashboardFilterActionType.clearAll })
            }}
          />
        ) : null}
      </Stack>

      <DashboardChartContainer
        chargingSessions={data || []}
        dispatch={dispatch}
        dashboardState={dashboardState}
        unfilteredData={unfilteredData || []}
        currencyCode={currencyCode}
      />

      <StatisticCards sessions={data || []} includesBiDiSessions={includesBiDiSessions} metric={dashboardMetric} currencyCode={currencyCode} />
    </Stack>
  )
}
